import { useCallback } from "react";
import { useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import {
  submitExternalProviderEvaluation,
  resetSubmitExternalProviderEvaluation,
} from "@academy-context/write/application/use-cases/external-provider/evaluation-submission/submit-evaluation";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import { selectExternalProviderEvaluationSubmission } from "@academy-context/write/application/use-cases/external-provider/evaluation-submission/selectors/external-provider-evaluation-submission-selectors";
import {
  toEvaluationPayload,
  type FormInputs,
} from "@academy-context/primary/shared/training-item-evaluation/form-validation/training-item-evaluation.schema";

export const useEvaluationSubmit = (form: ActionForm<FormInputs>, onSuccess: () => void, participationId: string) => {
  const run = useApiAction();
  const repository = useExternalProviderRepository();
  const { processing } = useAppSelector(selectExternalProviderEvaluationSubmission);
  useResetWhenSettled(processing, resetSubmitExternalProviderEvaluation);

  // L'API dit pourquoi l'évaluation est refusée (objectifs à évaluer changés entre-temps)
  const submit = useCallback(
    async (values: FormInputs) => {
      const outcome = await run(
        submitExternalProviderEvaluation({ participationId, body: toEvaluationPayload(values), externalProviderRepository: repository }),
        {
          success: "Évaluation soumise avec succès !",
          failure: "Erreur lors de la soumission de l'évaluation",
        },
      );
      if (!outcome.ok) {
        applyApiFieldErrors(form, outcome.error);
        return;
      }
      // Ce qui vient d'être rendu devient l'état de référence : le formulaire cesse d'être sale
      form.reset(values);
      onSuccess();
    },
    [run, participationId, repository, form, onSuccess],
  );

  return { submit };
};
